/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AddOnFilterColumns,
  AddOnFilterModel,
  useAddOnFilter,
  useSetAddOnFilter,
  useValidateAddOnListOrder,
} from '@gripp/shared-logic';
import {
  ColumnSelection,
  DropdownFooter,
  FilterType,
  FormContext,
  filterStyles as styles,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView, View } from 'react-native';
import { array, string, object as yupObject } from 'yup';

type AddOnFilterProps = {
  onCloseSideBar: () => void;
  type: FilterType;
};

export const AddOnFilter = (props: AddOnFilterProps) => {
  const { data: addOnFilter } = useAddOnFilter();
  const { mutateAsync: setAddOnFilter } = useSetAddOnFilter();
  const validateListOrder = useValidateAddOnListOrder();

  const formSchema = yupObject({
    columns: array().of(string().optional().nullable()).optional().nullable(),
  });
  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      columns: addOnFilter?.columns,
    },
  });

  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = formMethods;

  useEffect(() => {
    reset({
      columns: addOnFilter?.columns,
    });
  }, [addOnFilter, reset]);

  const selectedColumns = watch('columns');

  const isColumnsFilter = props.type === FilterType.ADDONCOLUMNS;

  const resetForm = useCallback(() => {
    reset({
      ...selectedColumns,
      ...(isColumnsFilter && { columns: new AddOnFilterModel().columns }),
    });
  }, [isColumnsFilter, reset, selectedColumns]);

  const canReset = (): boolean => {
    return !Object.values(getValues()).every((value) => value === undefined);
  };

  const applyFilter = async (filterModel: any) => {
    await Promise.all([
      setAddOnFilter(filterModel),
      validateListOrder(filterModel),
    ]);

    props.onCloseSideBar();
  };

  const onReset = async () => {
    const model = new AddOnFilterModel();
    const updatedFields = {
      ...(isColumnsFilter && { columns: model.columns }),
    };

    resetForm();

    const mergedFilters = { ...addOnFilter, ...updatedFields };
    await applyFilter(new AddOnFilterModel(mergedFilters));
  };

  const onSubmit = async (data: any) => {
    const model = new AddOnFilterModel({
      ...addOnFilter,
      ...data,
    });
    await applyFilter(model);
  };

  const renderStandardData = () => {
    return (
      <ColumnSelection
        baseColumns={AddOnFilterColumns}
        adminColumns={[]}
        i18nPrefix="addOn.filter"
      />
    );
  };

  return (
    <FormContext.Provider value={formMethods}>
      <View style={styles.container}>
        <View style={[styles.scrollViewContainer, { marginBottom: 30 }]}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isColumnsFilter && renderStandardData()}
          </ScrollView>
        </View>
        <DropdownFooter
          isDirty={isDirty}
          onSubmit={handleSubmit(onSubmit)}
          canReset={canReset}
          onReset={onReset}
        />
      </View>
    </FormContext.Provider>
  );
};
